/**
 * fxStoreCache — the app's FX store, built from the data Worker's
 * /api/symbols/fx (WORK.md item 52 step A2) and held for the session, where
 * the app had read the `fxStore` blob market-data's mirror wrote into VIP's KV.
 *
 * One read serves every page: the store is built once, through
 * lib/fxRoute.ts for FX_CURRENCIES, and the same promise is handed to every
 * caller for FX_CACHE_TTL_MS, so the portfolio pages and the research pages
 * read one object (lib/calc/fx.ts caches each series' sorted dates by
 * identity). The Worker caches its answer for 12 h, so a reload asks it once
 * more, never the warehouse.
 *
 * A failure is not cached: a non-2xx or an answer not of the route's shape
 * throws PortfolioApiError, as a failed blob read did, and the next call
 * reads again. A currency the Worker holds no series for is absent from the
 * store and named once in the console; `fxRate` answers null for it, so the
 * pages mark it "no FX rate" rather than convert it at 1.
 */
import { PortfolioApiError } from './kvBlob';
import type { FxStore } from './fx';
import { buildFxStore, FxAnswerError } from '../../../lib/fxRoute';

/** How long one built store is served before the next call reads again. */
export const FX_CACHE_TTL_MS = 30 * 60 * 1000;

let cached: { at: number; promise: Promise<FxStore> } | null = null;

async function getJson(path: string): Promise<unknown> {
  const resp = await fetch(path);
  if (!resp.ok) throw new PortfolioApiError(`${path.split('?')[0]} returned ${resp.status}`, resp.status);
  return resp.json();
}

async function build(): Promise<FxStore> {
  try {
    const { store, missing } = await buildFxStore(getJson);
    if (missing.length > 0) {
      console.warn(`FX: no series for ${missing.join(', ')}; amounts in them are left out of base-currency totals`);
    }
    return store;
  } catch (e) {
    if (e instanceof FxAnswerError) throw new PortfolioApiError(`/api/symbols/fx: ${e.message}`, 502);
    throw e;
  }
}

/** The USD-pivot FX store (currency → date → USD per unit), built from the Worker's route. */
export function getFxStore(): Promise<FxStore> {
  const now = Date.now();
  if (cached && now - cached.at < FX_CACHE_TTL_MS) return cached.promise;
  const entry = { at: now, promise: build() };
  cached = entry;
  entry.promise.catch(() => { if (cached === entry) cached = null; });
  return entry.promise;
}

/** Tests only: forget the built store. */
export function resetFxStoreCache(): void {
  cached = null;
}
